import React, { useEffect, useState, useCallback } from 'react';
import { useToast } from '../components/Toast';

const API = '/api';
const TOP50 = 'BTCUSDT,ETHUSDT,SOLUSDT,BNBUSDT,XRPUSDT,DOGEUSDT,ADAUSDT,AVAXUSDT,DOTUSDT,LINKUSDT,ATOMUSDT,NEARUSDT,SUIUSDT,ARBUSDT,OPUSDT,LTCUSDT,AAVEUSDT,INJUSDT,WIFUSDT,MATICUSDT,UNIUSDT,APTUSDT,FILUSDT,ICPUSDT,HBARUSDT,TRXUSDT,TONUSDT,XLMUSDT,FTMUSDT,BCHUSDT,ETCUSDT,MKRUSDT,GRTUSDT,RUNEUSDT,STXUSDT,IMXUSDT,SEIUSDT,TIAUSDT,JUPUSDT,PENDLEUSDT,ONDOUSDT,DYDXUSDT,GMXUSDT,TAOUSDT,PYTHUSDT,ENAUSDT,WLDUSDT,RENDERUSDT,FETUSDT,PEPEUSDT';

interface Profile { symbol:string; date:string; vah:number; val:number; poc:number; high:number; low:number; tick_size:number; total_tpos:number; value_area_pct:number; num_periods:number; }
interface Alert { alert_triggered:boolean; alert_type:string; classification:string; poc_relation:string; symbol:string; current_price:number; today_open:number; yesterday:Profile; distance_to_poc:number; distance_to_poc_pct:number; value_area_width:number; open_position_in_va:number; probability_rotation:number; trade_bias:string; message:string; }

const cs: Record<string, React.CSSProperties> = {
  page:{padding:0},
  header:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,flexWrap:'wrap',gap:12},
  title:{fontSize:28,fontWeight:700,color:'#fff',display:'flex',alignItems:'center',gap:12},
  badge:{background:'#8b5cf6',color:'#fff',fontSize:12,fontWeight:600,padding:'2px 10px',borderRadius:20},
  btn:{background:'#8b5cf6',color:'#fff',border:'none',padding:'10px 20px',borderRadius:8,fontWeight:600,cursor:'pointer',fontSize:14},
  btnSm:{border:'none',padding:'6px 14px',borderRadius:8,fontWeight:600,cursor:'pointer',fontSize:12},
  filterRow:{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'},
  grid:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(min(380px, 100%), 1fr))',gap:14,marginBottom:24},
  card:{background:'#13172b',border:'1px solid #1e2539',borderRadius:14,padding:18,position:'relative'},
  levels:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(90px, 1fr))',gap:8,marginBottom:12},
  level:{textAlign:'center',padding:'6px 0',borderRadius:8,background:'#0d1117'},
  vaBar:{height:24,borderRadius:8,position:'relative',background:'#0d1117',marginBottom:12,overflow:'hidden'},
  biasBox:{background:'#0d111799',borderRadius:8,padding:10,fontSize:11,lineHeight:1.5,color:'#94a3b8'},
  metric:{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:11},
  tgBox:{background:'#13172b',border:'1px solid #8b5cf644',borderRadius:14,padding:20,marginBottom:20},
  input:{background:'#0d1117',color:'#fff',border:'1px solid #1e2539',padding:'8px 12px',borderRadius:8,fontSize:13,width:'100%'},
  loading:{display:'flex',alignItems:'center',justifyContent:'center',padding:60,color:'#64748b'},
};

const FILTERS = [
  {key:'all',label:'Alle',c:'#8b5cf6'},
  {key:'open_inside_value',label:'Inside VA',c:'#f59e0b'},
  {key:'open_above_value',label:'Above VA',c:'#22c55e'},
  {key:'open_below_value',label:'Below VA',c:'#ef4444'},
];

const TpoPage: React.FC = () => {
  const toast = useToast();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [symbols] = useState(TOP50);
  const [showTg, setShowTg] = useState(false);
  const [tgChat, setTgChat] = useState('');
  const [tgToken, setTgToken] = useState('');
  const [tgEnabled, setTgEnabled] = useState(false);
  const [tgFilter, setTgFilter] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(API + '/tpo/scan?symbols=' + symbols);
      if (r.ok) {
        setAlerts(await r.json());
        setLastUpdate(new Date().toLocaleString('de-DE', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}));
      }
    } catch (e: any) { toast.error('Fehler', e.message); }
    finally { setLoading(false); }
  }, [symbols, toast]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { const i = setInterval(loadData, 300000); return () => clearInterval(i); }, [loadData]);
  useEffect(() => {
    fetch(API + '/tpo/telegram/settings/get').then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setTgChat(d.chat_id || ''); setTgToken(d.bot_token || ''); setTgEnabled(d.enabled || false); } }).catch(() => {});
  }, []);

  const filtered = alerts.filter(a => {
    if (filter !== 'all' && a.alert_type !== filter) return false;
    if (search && !a.symbol.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts: Record<string, number> = {
    all: alerts.length,
    open_inside_value: alerts.filter(a => a.alert_type === 'open_inside_value').length,
    open_above_value: alerts.filter(a => a.alert_type === 'open_above_value').length,
    open_below_value: alerts.filter(a => a.alert_type === 'open_below_value').length,
  };

  // ====== TELEGRAM FUNCTIONS ======
  const NL = String.fromCharCode(10);

  const buildReport = (): string => {
    const m = alerts.filter(a => tgFilter === 'all' || a.alert_type === tgFilter);
    if (!m.length) return '';
    const inside = m.filter(a => a.alert_type === 'open_inside_value');
    const above = m.filter(a => a.alert_type === 'open_above_value');
    const below = m.filter(a => a.alert_type === 'open_below_value');
    let msg = '\u{1F4CA} <b>TPO DAILY REPORT</b>' + NL;
    msg += '\u{1F4C5} ' + new Date().toLocaleDateString('de-DE') + ' | ' + m.length + ' Assets' + NL + NL;
    if (inside.length) {
      msg += '\u{2696}\u{FE0F} <b>INSIDE VALUE AREA (' + inside.length + ')</b>' + NL + '<pre>';
      for (const a of inside) {
        const sym = a.symbol.replace('USDT', '').padEnd(8);
        const price = ('$' + a.today_open.toFixed(2)).padStart(12);
        const poc = a.poc_relation.includes('above') ? '\u2B06POC' : '\u2B07POC';
        msg += sym + price + ' ' + poc + ' ' + a.probability_rotation.toFixed(0) + '%' + NL;
      }
      msg += '</pre>' + NL;
    }
    if (above.length) {
      msg += '\u{1F680} <b>ABOVE VALUE AREA (' + above.length + ')</b>' + NL + '<pre>';
      for (const a of above) {
        msg += a.symbol.replace('USDT', '').padEnd(8) + ('$' + a.today_open.toFixed(2)).padStart(12) + ' Long Bias' + NL;
      }
      msg += '</pre>' + NL;
    }
    if (below.length) {
      msg += '\u{1F4C9} <b>BELOW VALUE AREA (' + below.length + ')</b>' + NL + '<pre>';
      for (const a of below) {
        msg += a.symbol.replace('USDT', '').padEnd(8) + ('$' + a.today_open.toFixed(2)).padStart(12) + ' Short Bias' + NL;
      }
      msg += '</pre>';
    }
    return msg;
  };

  const sendReport = async () => {
    if (!tgChat || !tgToken) { toast.warning('Chat ID und Token eingeben!'); return; }
    const msg = buildReport();
    if (!msg) { toast.warning('Keine Alerts'); return; }
    try {
      const r = await fetch(API + '/tpo/telegram/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: tgChat, bot_token: tgToken, message: msg }),
      });
      const data = await r.json();
      if (data.success) toast.success('Report gesendet!');
      else toast.error('Fehler beim Senden');
    } catch { toast.error('Telegram Fehler'); }
  };

  const testConnection = async () => {
    if (!tgChat || !tgToken) { toast.warning('Chat ID und Token eingeben!'); return; }
    try {
      const r = await fetch(API + '/tpo/telegram/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: tgChat, bot_token: tgToken }),
      });
      if (r.ok) toast.success('Verbindung OK!');
      else toast.error('Fehler');
    } catch { toast.error('Fehler'); }
  };

  const saveSettings = async () => {
    try {
      await fetch(API + '/tpo/telegram/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: tgChat, bot_token: tgToken, enabled: tgEnabled, symbols: symbols.split(',') }),
      });
      toast.success('Gespeichert!');
    } catch { toast.error('Fehler'); }
  };

  const badge = (t: string) => ({
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: t === 'open_inside_value' ? '#f59e0b22' : t === 'open_above_value' ? '#22c55e22' : '#ef444422',
    color: t === 'open_inside_value' ? '#f59e0b' : t === 'open_above_value' ? '#22c55e' : '#ef4444',
  } as React.CSSProperties);

  return (
    <div style={cs.page}>
      <div style={cs.header}>
        <div style={cs.title}>{'\u{1F4CA}'} TPO / Market Profile <span style={cs.badge}>Live</span>
          {lastUpdate && <span style={{fontSize:12,color:'#64748b',fontWeight:400}}>{lastUpdate}</span>}</div>
        <div style={{display:'flex',gap:8}}>
          <button style={{...cs.btnSm,background:showTg?'#8b5cf633':'#1e293b',color:showTg?'#8b5cf6':'#64748b',border:'1px solid '+(showTg?'#8b5cf6':'#334155')}} onClick={() => setShowTg(!showTg)}>{'\u{1F514}'} Telegram</button>
          <button style={cs.btn} onClick={loadData}>{loading ? '\u23F3 Scanning...' : '\u{1F504} Scan'}</button>
        </div>
      </div>

      {showTg && (
        <div style={cs.tgBox}>
          <div style={{color:'#8b5cf6',fontWeight:700,marginBottom:12,fontSize:14}}>{'\u{1F514}'} Telegram Notifications</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:12,marginBottom:12}}>
            <div><label style={{color:'#64748b',fontSize:11,fontWeight:600}}>Bot Token</label>
              <input style={cs.input} value={tgToken} onChange={e => setTgToken(e.target.value)} placeholder="123456:ABC-DEF..." /></div>
            <div><label style={{color:'#64748b',fontSize:11,fontWeight:600}}>Chat ID</label>
              <input style={cs.input} value={tgChat} onChange={e => setTgChat(e.target.value)} placeholder="5257879535" /></div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:14}}>
            <label style={{color:'#94a3b8',fontSize:13,display:'flex',alignItems:'center',gap:6}}>
              <input type="checkbox" checked={tgEnabled} onChange={e => setTgEnabled(e.target.checked)} /> Auto (02:02)</label>
            <button style={{...cs.btnSm,background:'#1e293b',color:'#94a3b8',border:'1px solid #334155'}} onClick={testConnection}>{'\u{1F4E4}'} Test</button>
            <button style={{...cs.btnSm,background:'#22c55e',color:'#000'}} onClick={saveSettings}>{'\u{1F4BE}'} Speichern</button>
          </div>
          <div style={{borderTop:'1px solid #1e2539',paddingTop:14}}>
            <div style={{color:'#f59e0b',fontWeight:700,marginBottom:8,fontSize:12}}>{'\u{1F4E4}'} Manueller Report</div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <select style={{...cs.input,width:'auto'}} value={tgFilter} onChange={e => setTgFilter(e.target.value)}>
                <option value="all">Alle ({counts.all})</option>
                <option value="open_inside_value">Inside VA ({counts.open_inside_value})</option>
                <option value="open_above_value">Above VA ({counts.open_above_value})</option>
                <option value="open_below_value">Below VA ({counts.open_below_value})</option>
              </select>
              <button style={{...cs.btnSm,background:'#f59e0b',color:'#000',fontWeight:700}} onClick={sendReport}>{'\u{1F4E4}'} Report senden</button>
            </div>
          </div>
        </div>
      )}

      <div style={cs.filterRow}>
        <input style={{...cs.input,flex:1,maxWidth:300}} value={search} onChange={e => setSearch(e.target.value)} placeholder="\u{1F50D} Symbol suchen..." />
        {FILTERS.map(f => (
          <button key={f.key} style={{...cs.btnSm,background:filter===f.key?f.c+'33':f.c+'11',color:filter===f.key?f.c:'#64748b',border:'1px solid '+(filter===f.key?f.c+'66':'#1e2539')}} onClick={() => setFilter(f.key)}>
            {f.label} ({counts[f.key] || 0})</button>
        ))}
      </div>

      <div style={cs.grid}>
        {filtered.map(a => {
          const vaW = a.yesterday.vah - a.yesterday.val;
          const openPos = a.open_position_in_va * 100;
          const pocPos = vaW > 0 ? ((a.yesterday.poc - a.yesterday.val) / vaW) * 100 : 50;
          const sym = a.symbol.replace('USDT', '');
          return (
            <div key={a.symbol} style={{...cs.card, borderLeft: '3px solid ' + (a.alert_type === 'open_inside_value' ? '#f59e0b' : a.alert_type === 'open_above_value' ? '#22c55e' : '#ef4444')}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <div><span style={{fontSize:18,fontWeight:700,color:'#fff'}}>{sym}</span>
                  <span style={{color:'#475569',fontSize:10,marginLeft:8}}>Heute {new Date().toLocaleDateString('de-DE')} | VA vom {a.yesterday.date}</span></div>
                <span style={badge(a.alert_type)}>{a.alert_type === 'open_inside_value' ? '\u2696\uFE0F Inside' : a.alert_type === 'open_above_value' ? '\u{1F680} Above' : '\u{1F4C9} Below'}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                <div><div style={{color:'#64748b',fontSize:10,fontWeight:600}}>OPEN</div><div style={{color:'#fff',fontSize:15,fontWeight:700}}>${a.today_open.toLocaleString(undefined, {maximumFractionDigits: 4})}</div></div>
                <div style={{textAlign:'right'}}><div style={{color:'#64748b',fontSize:10,fontWeight:600}}>CURRENT</div><div style={{color: a.current_price >= a.today_open ? '#22c55e' : '#ef4444', fontSize: 15, fontWeight: 700}}>${a.current_price.toLocaleString(undefined, {maximumFractionDigits: 4})}</div></div>
              </div>
              <div style={cs.levels}>
                <div style={{...cs.level, borderTop: '2px solid #22c55e'}}><div style={{fontSize:9,color:'#64748b',fontWeight:600}}>VAH</div><div style={{fontSize:14,fontWeight:700,color:'#22c55e',marginTop:2}}>${a.yesterday.vah.toLocaleString(undefined, {maximumFractionDigits: 4})}</div></div>
                <div style={{...cs.level, borderTop: '2px solid #f59e0b'}}><div style={{fontSize:9,color:'#64748b',fontWeight:600}}>POC</div><div style={{fontSize:14,fontWeight:700,color:'#f59e0b',marginTop:2}}>${a.yesterday.poc.toLocaleString(undefined, {maximumFractionDigits: 4})}</div></div>
                <div style={{...cs.level, borderTop: '2px solid #ef4444'}}><div style={{fontSize:9,color:'#64748b',fontWeight:600}}>VAL</div><div style={{fontSize:14,fontWeight:700,color:'#ef4444',marginTop:2}}>${a.yesterday.val.toLocaleString(undefined, {maximumFractionDigits: 4})}</div></div>
              </div>
              <div style={cs.vaBar}>
                <div style={{position:'absolute',left:0,right:0,top:0,bottom:0,background:'linear-gradient(to right,#ef444422,#f59e0b22,#22c55e22)',borderRadius:8}} />
                <div style={{position:'absolute',left:pocPos+'%',top:0,bottom:0,width:2,background:'#f59e0b',zIndex:2}} />
                {a.alert_type === 'open_inside_value' && <div style={{position:'absolute',left:Math.max(2, Math.min(98, openPos))+'%',top:2,bottom:2,width:10,marginLeft:-5,background:'#fff',borderRadius:4,zIndex:3}} />}
              </div>
              <div style={{marginBottom:10}}>
                <div style={cs.metric}><span style={{color:'#64748b'}}>Rotation</span><span style={{color: a.probability_rotation >= 60 ? '#22c55e' : '#f59e0b', fontWeight:600}}>{a.probability_rotation.toFixed(0)}%</span></div>
                <div style={cs.metric}><span style={{color:'#64748b'}}>POC</span><span style={{color: a.poc_relation.includes('above') ? '#22c55e' : '#ef4444', fontWeight:600}}>{a.poc_relation.includes('above') ? 'Above' : 'Below'}</span></div>
                <div style={cs.metric}><span style={{color:'#64748b'}}>VA Width</span><span style={{color:'#e2e8f0',fontWeight:600}}>${vaW.toFixed(2)}</span></div>
                <div style={cs.metric}><span style={{color:'#64748b'}}>Range</span><span style={{color:'#e2e8f0',fontWeight:600}}>${a.yesterday.high?.toFixed(2)} - ${a.yesterday.low?.toFixed(2)}</span></div>
              </div>
              <div style={cs.biasBox}><span style={{color:'#8b5cf6',fontWeight:700,fontSize:10}}>BIAS: </span>{a.trade_bias}</div>
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && !loading && <div style={{background:'#13172b',borderRadius:14,padding:40,textAlign:'center',color:'#64748b'}}>Keine Ergebnisse</div>}
      {loading && !alerts.length && <div style={cs.loading}>{'\u{1F4CA}'} Scanne Assets...</div>}
    </div>
  );
};

export default TpoPage;
